// Pure renderer — worker management has moved to VisionDetector / VisionWorker.
// Receives mask data from VisionDetector.detect() and composites it onto the
// canvas. Also caches the last mask so it can be re-applied on frames where
// segmentation didn't run (throttled).

export interface SegmentationRenderContext {
  canvasElement: HTMLCanvasElement;
  canvasCtx: CanvasRenderingContext2D;
  getVideoDrawDims: () => { w: number; h: number };
  applyRotationTransform: (ctx: CanvasRenderingContext2D) => void;
  renderOverlayControls: () => void;
  renderWidgetControls: () => void;
  perfMetrics: {
    segmentationTime: number;
    modelLoadCount: number;
    segWorkerLatencyMs: number;
  };
  updateDetectionHistory: (
    type: "hand" | "pose" | "postProcessing" | "segmentation",
    time: number,
  ) => void;
}

const MASK_STALE_THRESHOLD = 500;

export class SegmentationManager {
  private _enabled = false;
  private flipHorizontal = false;
  private ctx: SegmentationRenderContext;
  private maskLastUpdated = 0;

  // Reusable mask canvas to avoid creating a new one every frame.
  private maskCanvas: HTMLCanvasElement | undefined;
  private maskCtx: CanvasRenderingContext2D | undefined;

  // Reference to the video element used in the most recent renderResult() call,
  // used by renderWithCachedMask() to re-composite cached mask over current frame.
  private lastVideo: HTMLVideoElement | undefined;

  // Offscreen canvas for destination-in compositing.
  // We do the compositing here (guaranteed alpha:true) then blit to main canvas.
  private _offscreenCanvas: HTMLCanvasElement | undefined;
  private _offscreenCtx: CanvasRenderingContext2D | undefined;

  constructor(renderContext: SegmentationRenderContext) {
    this.ctx = renderContext;
  }

  applyOptions(
    opts: { segmentation: boolean; cameraFlipHorizontal: boolean },
  ): void {
    this._enabled = opts.segmentation;
    this.flipHorizontal = opts.cameraFlipHorizontal;
    if (!this._enabled) {
      // Reset stale tracking when disabled so isActive() returns false immediately.
      this.maskLastUpdated = 0;
    }
  }

  /** Call when VisionDetector.detect() returns a segmentation result. */
  renderResult(
    maskData: Uint8Array,
    maskWidth: number,
    maskHeight: number,
    video: HTMLVideoElement,
  ): void {
    if (!this._enabled) return;
    this.maskLastUpdated = performance.now();
    this.lastVideo = video;
    this.buildMaskCanvas(maskData, maskWidth, maskHeight);
    this.composite(video);
  }

  /**
   * Re-composite the cached mask over the current video frame.
   * Call on frames where segmentation was throttled but isActive() is true.
   */
  renderWithCachedMask(video: HTMLVideoElement): void {
    if (!this._enabled || !this.maskCanvas) return;
    this.composite(video);
  }

  isActive(): boolean {
    return !!(
      this._enabled &&
      this.maskCanvas &&
      this.maskLastUpdated > 0 &&
      (performance.now() - this.maskLastUpdated) < MASK_STALE_THRESHOLD
    );
  }

  reset(): void {
    this.maskLastUpdated = 0;
  }

  dispose(): void {
    this.maskLastUpdated = 0;
    this.maskCanvas = undefined;
    this.maskCtx = undefined;
    this.lastVideo = undefined;
    this._offscreenCanvas = undefined;
    this._offscreenCtx = undefined;
  }

  private ensureMaskCanvas(width: number, height: number): void {
    if (
      !this.maskCanvas ||
      this.maskCanvas.width !== width ||
      this.maskCanvas.height !== height
    ) {
      this.maskCanvas = document.createElement("canvas");
      this.maskCanvas.width = width;
      this.maskCanvas.height = height;
      // Explicit alpha:true so putImageData alpha=0 produces transparent pixels.
      this.maskCtx = this.maskCanvas.getContext("2d", { alpha: true })!;
    }
  }

  private ensureOffscreenCanvas(width: number, height: number): void {
    if (
      !this._offscreenCanvas ||
      this._offscreenCanvas.width !== width ||
      this._offscreenCanvas.height !== height
    ) {
      this._offscreenCanvas = document.createElement("canvas");
      this._offscreenCanvas.width = width;
      this._offscreenCanvas.height = height;
      // Must be alpha:true — destination-in compositing requires a canvas that
      // can hold transparent pixels. We own this canvas so it is always alpha:true.
      this._offscreenCtx = this._offscreenCanvas.getContext("2d", {
        alpha: true,
      })!;
    }
  }

  private buildMaskCanvas(
    maskData: Uint8Array,
    maskWidth: number,
    maskHeight: number,
  ): void {
    this.ensureMaskCanvas(maskWidth, maskHeight);
    // selfie_segmenter: category 0 = person (opaque), 1 = background (transparent).
    const imageData = new ImageData(maskWidth, maskHeight);
    for (let i = 0; i < maskData.length; i++) {
      imageData.data[i * 4 + 3] = maskData[i] === 0 ? 255 : 0;
    }
    this.maskCtx!.putImageData(imageData, 0, 0);
  }

  private composite(video: HTMLVideoElement): void {
    const { canvasElement, canvasCtx } = this.ctx;
    const { w: drawW, h: drawH } = this.ctx.getVideoDrawDims();
    const segStart = performance.now();

    // Step 1: composite video + mask on an offscreen canvas.
    // The offscreen canvas is always alpha:true (we created it), so destination-in
    // is guaranteed to produce transparent pixels for background regions —
    // regardless of whether the main canvas context has alpha:true or not.
    this.ensureOffscreenCanvas(drawW, drawH);
    const offCtx = this._offscreenCtx!;
    offCtx.globalCompositeOperation = "source-over";
    offCtx.clearRect(0, 0, drawW, drawH);
    offCtx.drawImage(video, 0, 0, drawW, drawH);
    offCtx.globalCompositeOperation = "destination-in";
    offCtx.drawImage(this.maskCanvas!, 0, 0, drawW, drawH);
    offCtx.globalCompositeOperation = "source-over"; // reset for next frame

    // Step 2: blit the composited result to the main canvas with flip / rotation.
    // Using "copy" composite operation on the main canvas clears it and draws the
    // offscreen result in one step, preserving transparent pixels as alpha=0 in
    // the main canvas bitmap (requires main canvas alpha:true to render transparently).
    canvasCtx.globalCompositeOperation = "source-over";
    canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);
    canvasCtx.save();
    if (!this.flipHorizontal) {
      canvasCtx.translate(canvasElement.width, 0);
      canvasCtx.scale(-1, 1);
    }
    this.ctx.applyRotationTransform(canvasCtx);
    canvasCtx.drawImage(this._offscreenCanvas!, 0, 0, drawW, drawH);
    canvasCtx.restore();

    this.ctx.renderOverlayControls();
    this.ctx.renderWidgetControls();

    this.ctx.perfMetrics.segmentationTime = performance.now() - segStart;
    this.ctx.updateDetectionHistory(
      "segmentation",
      this.ctx.perfMetrics.segmentationTime,
    );
  }
}
